import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { CalendarIcon, Edit, X } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { DatePickerV2 } from "@/components/fields/date-picker";
import { ComboboxAsync } from "@/components/combobox/combobox-async";

import { OrganizeData } from "./dynamic-drawer-content";
import { JobOrderAttributeDataType } from "../../list/constant";
import { useForeignKeyData } from "@/hooks/use-foreign-key-data";

import { cn, debounce } from "@/lib/utils";
import { isDateTimeWithClockField } from "@/lib/pps-date-time";
import { useWorkSpaceStore } from "@/stores/store";

type JobInfoItem = {
  label: string;
  value?: unknown;
  valueId?: unknown;
  attribute?: string;
  dataType?: JobOrderAttributeDataType | number;
  isSystemAttribute?: boolean;
  isEditable?: boolean;
  referenceTable?: string;
  nestedColumnName?: string;
};

const staticFieldOrder: Record<string, number> = {
  joborderno: 0,
  mainjoborder: 1,
  part: 2,
  serialnumber: 3,
  customer: 4,
  datein: 5,
  edd: 6,
  hoursclocked: 7,
  partnumber: 8,
  partdescription: 9,
};

function ForeignKeyField({
  item,
  onFieldChange,
  disabled,
}: {
  item: JobInfoItem;
  onFieldChange: (attribute: string, value: any) => void;
  disabled: boolean;
}) {
  const referenceTable = (item.referenceTable || "").trim();

  // Handle different valueId formats
  let foreignKeyValue;
  if (item.valueId !== undefined && item.valueId !== null) {
    // If valueId is an object with id property
    if (
      typeof item.valueId === "object" &&
      item.valueId !== null &&
      "id" in item.valueId
    ) {
      foreignKeyValue = item.valueId.id;
    } else {
      // If valueId is already a primitive (number/string)
      foreignKeyValue = item.valueId;
    }
  } else {
    // Fallback to value
    foreignKeyValue = item.value;
  }

  if (!referenceTable) {
    return (
      <div className="aero:space-y-1">
        <Input
          value={foreignKeyValue?.toString() || ""}
          disabled
          className="aero:w-full aero:h-9 aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground"
        />
        <p className="aero:text-xs aero:text-amber-600">
          Reference source is not configured for this field.
        </p>
      </div>
    );
  }

  return (
    <ForeignKeyCombobox
      item={item}
      onFieldChange={onFieldChange}
      disabled={disabled}
      foreignKeyValue={foreignKeyValue}
      referenceTable={referenceTable}
    />
  );
}

function ForeignKeyCombobox({
  item,
  onFieldChange,
  disabled,
  foreignKeyValue,
  referenceTable,
}: {
  item: JobInfoItem;
  onFieldChange: (attribute: string, value: any) => void;
  disabled: boolean;
  foreignKeyValue: unknown;
  referenceTable: string;
}) {
  const { optionsData, setSearch } = useForeignKeyData(
    referenceTable.slice(0, 1).toLowerCase() +
      referenceTable.slice(1, referenceTable.length),
    item.nestedColumnName || "",
  );

  return (
    <ComboboxAsync
      value={foreignKeyValue?.toString() || ""}
      labelValueKey="value"
      options={
        optionsData.map((item: { value: string | number; label: string }) => ({
          value: item.value.toString(),
          label: item.label,
        })) || []
      }
      onChange={(event) => {
        if (!item.attribute) {
          return;
        }

        onFieldChange(item.attribute, event.value);
      }}
      onSearchChange={setSearch}
      disabled={disabled}
      popOverClassName="aero:border aero:border-neutral-200 aero:bg-white aero:shadow-lg aero:rounded-md"
    />
  );
}

export function JobInformationContent({
  organizedData,
  onSave,
  loading,
  canManage = true, // WOR-1793: Add canManage prop
}: {
  organizedData: OrganizeData;
  onSave?: (body: Record<string, string | number | boolean>) => void;
  loading: boolean;
  canManage?: boolean;
}) {
  const { workspace } = useWorkSpaceStore();
  const [isEditing, setIsEditing] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  const normalizeKey = (value: string | undefined) =>
    (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

  const isDateTimeWithClock = (item: JobInfoItem) => {
    return isDateTimeWithClockField(item.attribute, item.label);
  };

  const getOrderKey = (item: JobInfoItem) => {
    const attributeKey = normalizeKey(item.attribute);
    const labelKey = normalizeKey(item.label);

    if (attributeKey in staticFieldOrder) {
      return attributeKey;
    }

    const match = Object.keys(staticFieldOrder).find(
      (key) => labelKey.includes(key) || attributeKey.includes(key),
    );

    return match || "";
  };

  const getItemKey = (item: JobInfoItem) =>
    `${item.attribute || ""}|${normalizeKey(item.label)}`;

  const allItems = useMemo<JobInfoItem[]>(() => {
    const rawItems = [
      ...organizedData.relationshipItems,
      ...organizedData.overviewItems,
      ...organizedData.dateTimeItems,
      ...organizedData.otherItems,
    ].map((item) => ({
      ...item,
      dataType: item.dataType ?? JobOrderAttributeDataType.TEXT,
    }));

    const isPartKey = (item: JobInfoItem) =>
      normalizeKey(item.attribute) === "part";
    const isPartNumberKey = (item: JobInfoItem) => {
      const attributeKey = normalizeKey(item.attribute);
      const labelKey = normalizeKey(item.label);
      return attributeKey === "partnumber" || labelKey.includes("partnumber");
    };
    const isPartDescriptionKey = (item: JobInfoItem) => {
      const attributeKey = normalizeKey(item.attribute);
      const labelKey = normalizeKey(item.label);
      return (
        attributeKey === "partdescription" ||
        labelKey.includes("partdescription")
      );
    };

    const partItems = rawItems.filter(isPartKey);
    const standalonePartItem = partItems.find(
      (item) => !isPartNumberKey(item) && !isPartDescriptionKey(item),
    );
    const partNumberItem = rawItems.find(isPartNumberKey);
    const partDescriptionItem = rawItems.find(isPartDescriptionKey);
    const partItem =
      standalonePartItem || partNumberItem || partDescriptionItem;

    // Merge when part variants exist and we can bind to the canonical `part` attribute.
    if (!partItem || partItems.length === 0) {
      return rawItems;
    }

    const mergedPartValue = [partNumberItem?.value, partDescriptionItem?.value]
      .filter(
        (value) =>
          value !== null && value !== undefined && String(value).trim() !== "",
      )
      .map((value) => String(value))
      .join(" - ");

    const mergedPartItem = {
      ...partItem,
      label: "Part",
      value: mergedPartValue || partItem.value,
      attribute: "part",
      dataType: partItem.dataType ?? JobOrderAttributeDataType.TEXT,
    };

    const hasPartVariants =
      Boolean(partNumberItem) ||
      Boolean(partDescriptionItem) ||
      partItems.length > 1;

    if (!hasPartVariants) {
      return rawItems;
    }

    const firstPartRelatedIndex = rawItems.findIndex(
      (item) =>
        isPartKey(item) || isPartNumberKey(item) || isPartDescriptionKey(item),
    );

    const filteredItems = rawItems.filter(
      (item) =>
        !isPartKey(item) &&
        !isPartNumberKey(item) &&
        !isPartDescriptionKey(item),
    );

    const insertIndex = Math.max(0, firstPartRelatedIndex);
    filteredItems.splice(insertIndex, 0, mergedPartItem);

    return filteredItems;
  }, [organizedData]);

  const basicInfoItems = useMemo<JobInfoItem[]>(() => {
    return allItems
      .filter(
        (item) => item.isSystemAttribute === true || Boolean(getOrderKey(item)),
      )
      .sort((left, right) => {
        const leftKey = getOrderKey(left);
        const rightKey = getOrderKey(right);

        return (
          (staticFieldOrder[leftKey] ?? 99) - (staticFieldOrder[rightKey] ?? 99)
        );
      });
  }, [allItems]);

  const additionalInfoItems = useMemo<JobInfoItem[]>(() => {
    const excluded = new Set(basicInfoItems.map((item) => getItemKey(item)));
    const additional = allItems.filter(
      (item) => !excluded.has(getItemKey(item)),
    );

    const priorityOrder = ["datein", "estimatedduedate", "startdate"];
    const score = (item: JobInfoItem) => {
      const key = `${normalizeKey(item.label)}${normalizeKey(item.attribute)}`;
      const matchIndex = priorityOrder.findIndex((priority) =>
        key.includes(priority),
      );
      return matchIndex >= 0 ? matchIndex : Number.MAX_SAFE_INTEGER;
    };

    return [...additional].sort((left, right) => score(left) - score(right));
  }, [allItems, basicInfoItems]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleFieldChange = (attribute: string, value: any) => {
    if (onSave) {
      const body = {
        [attribute]: value,
        workspaceId: workspace?.id || null,
      };
      onSave(body);
      setLastSaved(new Date().toLocaleTimeString());
    }
  };

  const toDateValue = (value: unknown): Date | undefined => {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      return value;
    }

    const parsed = dayjs(value as string | number | Date);
    return parsed.isValid() ? parsed.toDate() : undefined;
  };

  const renderEditableField = (item: JobInfoItem) => {
    const value = item.value || "";
    const updateItemValue = (nextValue: any) => {
      if (!item.attribute) {
        return;
      }

      handleFieldChange(item.attribute, nextValue);
    };

    switch (item.dataType) {
      case JobOrderAttributeDataType.TEXT:
        return (
          <Input
            type="text"
            className="aero:w-full aero:h-9 aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground aero:placeholder:text-neutral-500"
            defaultValue={value as string}
            onChange={debounce(
              (e: React.ChangeEvent<HTMLInputElement>) =>
                updateItemValue(e.target.value),
              1000,
            )}
            disabled={!isEditing || loading}
          />
        );

      case JobOrderAttributeDataType.INT:
      case JobOrderAttributeDataType.FRACTION:
        return (
          <Input
            type="number"
            className="aero:w-full aero:h-9 aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground aero:placeholder:text-neutral-500"
            defaultValue={value as number}
            onChange={debounce(
              (e: React.ChangeEvent<HTMLInputElement>) =>
                updateItemValue(Number(e.target.value)),
              1000,
            )}
            disabled={!isEditing || loading}
          />
        );

      case JobOrderAttributeDataType.BOOLEAN:
        return (
          <Switch
            checked={value === true || value === "true"}
            onCheckedChange={(checked) => updateItemValue(checked)}
            disabled={!isEditing || loading}
          />
        );
      case JobOrderAttributeDataType.FOREIGN_KEY: {
        return (
          <ForeignKeyField
            item={item}
            onFieldChange={handleFieldChange}
            disabled={!isEditing || loading}
          />
        );
      }
      case JobOrderAttributeDataType.DATETIME:
        if (isDateTimeWithClock(item)) {
          return (
            <DatePickerV2
              value={toDateValue(value) || null}
              onChange={(selectedDate) => {
                if (!selectedDate) {
                  updateItemValue("");
                  return;
                }

                updateItemValue(dayjs(selectedDate).format("YYYY-MM-DD HH:mm"));
              }}
              placeholder="Pick date and time"
              showTime={true}
              disabled={!isEditing || loading}
              className="aero:w-full"
            />
          );
        }

        return (
          <Popover modal={true}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                design="outline"
                intent="neutral"
                className={cn(
                  "aero:w-full aero:h-9 aero:justify-start aero:text-left aero:font-normal aero:text-sm",
                  !value && "aero:text-muted-foreground",
                )}
                disabled={!isEditing || loading}
              >
                <CalendarIcon className="aero:mr-2 aero:h-4 aero:w-4" />
                {value ? (
                  dayjs(value as Date).format("DD/MM/YYYY")
                ) : (
                  <span>dd/mm/yyyy</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="aero:w-auto aero:p-0"
              style={{ zIndex: 9999 }}
              side="bottom"
              align="start"
            >
              <Calendar
                mode="single"
                selected={toDateValue(value)}
                onSelect={(selectedDate) => {
                  if (selectedDate) {
                    updateItemValue(dayjs(selectedDate).format("YYYY-MM-DD"));
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );

      default:
        return (
          <Input
            type="text"
            className="aero:w-full aero:h-9 aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground aero:placeholder:text-neutral-500"
            defaultValue={value as string}
            onChange={debounce(
              (e: React.ChangeEvent<HTMLInputElement>) =>
                updateItemValue(e.target.value),
              1000,
            )}
            disabled={!isEditing || loading}
          />
        );
    }
  };
  const formatDisplayValue = (item: JobInfoItem) => {
    if (item.dataType === JobOrderAttributeDataType.BOOLEAN) {
      if (item.value === null || item.value === undefined) {
        return "-";
      }
      const boolValue = item.value === true || item.value === "true";
      return boolValue ? "Yes" : "No";
    }

    if (item.dataType === JobOrderAttributeDataType.DATETIME) {
      if (!item.value) {
        return "-";
      }

      return isDateTimeWithClock(item)
        ? dayjs(item.value as Date).format("DD/MM/YYYY HH:mm")
        : dayjs(item.value as Date).format("DD/MM/YYYY");
    }

    if (typeof item.value === "object" && item.value !== null) {
      if ("label" in item.value && item.value.label) {
        return String(item.value.label);
      }
      if ("name" in item.value && item.value.name) {
        return String(item.value.name);
      }
      return "-";
    }

    return item.value ? String(item.value) : "-";
  };

  const FieldGrid = ({
    items,
    emptyMessage,
    columns,
  }: {
    items: JobInfoItem[];
    emptyMessage: string;
    columns: "two" | "three";
  }) => {
    if (items.length === 0) {
      return (
        <div className="aero:rounded-md aero:border aero:border-dashed aero:border-neutral-300 aero:p-4 aero:text-sm aero:text-neutral-500">
          {emptyMessage}
        </div>
      );
    }

    const gridClassName =
      columns === "three"
        ? "aero:grid-cols-1 aero:md:grid-cols-3"
        : "aero:grid-cols-1 aero:md:grid-cols-2";

    return (
      <div className={cn("aero:grid aero:gap-4", gridClassName)}>
        {items.map((item, index) => (
          <div
            key={`${getItemKey(item)}-${index}`}
            className="aero:min-h-16 aero:space-y-1.5"
          >
            <p className="aero:text-sm aero:font-medium aero:text-neutral-900">
              {item.label}
            </p>
            {isEditing && item.attribute && item.isEditable !== false ? (
              <div className="aero:mt-2">{renderEditableField(item)}</div>
            ) : (
              <div className="aero:space-y-1">
                <p className="aero:text-sm aero:text-neutral-700 aero:break-words">
                  {formatDisplayValue(item)}
                </p>
                {isEditing && item.isEditable === false && (
                  <span className="aero:inline-flex aero:items-center aero:rounded-full aero:border aero:border-neutral-300 aero:px-2 aero:py-0.5 aero:text-[11px] aero:font-semibold aero:leading-none aero:text-neutral-600">
                    Read only
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="aero:p-6 aero:space-y-6">
      <div className="aero:flex aero:items-center aero:justify-between">
        <div className="aero:flex aero:items-center aero:gap-2">
          <div className="aero:h-7 aero:w-[3px] aero:rounded-full aero:bg-brand-primary-600" />
          <h3 className="aero:text-lg aero:font-bold aero:leading-7 aero:text-brand-primary-600">
            Basic Information
          </h3>
        </div>
        {canManage && (
          <Button
            onClick={isEditing ? handleCancel : handleEdit}
            intent="secondary"
            design="outline"
            type="button"
            size="sm"
            className="aero:h-9 aero:gap-2 aero:px-4"
          >
            {isEditing ? (
              <X className="aero:h-4 aero:w-4" />
            ) : (
              <Edit className="aero:h-4 aero:w-4" />
            )}
            <span>{isEditing ? "Done" : "Edit"}</span>
          </Button>
        )}
      </div>

      <FieldGrid
        items={basicInfoItems}
        emptyMessage="Basic information is not available yet."
        columns="two"
      />

      <div className="aero:space-y-4">
        <div className="aero:flex aero:items-center aero:gap-2">
          <div className="aero:h-7 aero:w-[3px] aero:rounded-full aero:bg-brand-primary-600" />
          <h3 className="aero:text-lg aero:font-bold aero:leading-7 aero:text-brand-primary-600">
            Additional Information
          </h3>
        </div>
        <FieldGrid
          items={additionalInfoItems}
          emptyMessage="Additional information is not available yet."
          columns="three"
        />
      </div>

      {lastSaved && isEditing && (
        <p className="aero:text-xs aero:text-neutral-500">
          Last saved {lastSaved}
        </p>
      )}
    </div>
  );
}
